// AccountSignIn.tsx — the one shared sign-in panel used by every entry point
// (LoginWall, the Today SignInTile, the onboarding account step, and the You
// screen). Passwordless by design: you enter your email, we send a 6-digit code,
// you type it in. No passwords, no third-party logins. A new email is created on
// first verify. Free accounts are not time-limited: you can explore much of the
// programme for free, and membership (bought on the website) unlocks everything.
import React from 'react';
import { Btn, Body } from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';
import { isReviewEmail } from '@/services/auth';
import { SHOW_PURCHASE, SHOW_IAP } from '@/config/supabase';

const field: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '13px 14px',
  borderRadius: 13,
  border: '1px solid var(--hair-strong)',
  background: 'var(--card-top)',
  color: 'var(--ink)',
  fontFamily: 'Inter, system-ui, sans-serif',
  fontSize: 15,
  letterSpacing: '-0.003em',
  outline: 'none',
};

export function AccountSignIn({ compact = false, mode = 'create' }: { compact?: boolean; mode?: 'create' | 'signin' }) {
  const auth = useAuth();
  // Name is collected ONLY when creating an account — a returning sign-in never asks.
  const collectName = mode === 'create';
  const [firstName, setFirstName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [otp, setOtp] = React.useState('');
  const [otpSent, setOtpSent] = React.useState(false);
  const [orgCode, setOrgCode] = React.useState('');
  // Forced organisation-code decision (create mode): 'unset' until the user answers
  // Yes/No, so workplace users can't accidentally skip redeeming. Gates the send button.
  const [hasOrgCode, setHasOrgCode] = React.useState<'unset' | 'yes' | 'no'>('unset');

  const [sending, setSending] = React.useState(false);
  const busy = auth.signingIn;
  const gap = compact ? 9 : 11;
  const size = compact ? 'md' : 'lg';
  const reset = () => auth.clearError();

  // APP REVIEW. One fixed address signs in with a PIN instead of an emailed code, because
  // a store reviewer cannot receive our one-time code and cannot be given a mailbox.
  // Recognising the address is all that happens here: the PIN itself is the account's
  // Supabase password, checked by Supabase, so nothing secret is in this bundle.
  const reviewMode = isReviewEmail(email);

  const onSendCode = async () => {
    if (sending) return; // ignore repeat taps while a send is already in flight
    reset();
    // The review account has no mailbox to send to; go straight to the PIN step.
    if (reviewMode) { setOtpSent(true); setOtp(''); return; }
    setSending(true);
    try {
      const ok = await auth.sendOtp(email.trim(), collectName ? firstName.trim() : undefined);
      if (ok) setOtp('');
      // Only ever advance INTO the code screen; a failed resend (e.g. the send
      // cooldown) must not bounce a user who's already there back to the email step.
      setOtpSent((prev) => prev || ok);
    } finally {
      setSending(false);
    }
  };
  const onVerify = () => {
    if (reviewMode) { void auth.confirmReviewPin(email.trim(), otp.trim()); return; }
    // The org code (if any) is applied AFTER sign-in; with none, the backend still
    // checks the verified email domain. See useAuth.confirmOtp.
    void auth.confirmOtp(email.trim(), otp.trim(), orgCode.trim(), collectName ? firstName.trim() : undefined);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap, width: '100%', textAlign: 'left' }}>
      {!otpSent ? (
        <>
          {collectName && (
            <>
              <input
                type="text"
                autoComplete="given-name"
                placeholder="Your first name (or a nickname)"
                aria-label="Your first name or nickname"
                value={firstName}
                onChange={(e) => { setFirstName(e.target.value.slice(0, 60)); reset(); }}
                style={field}
              />
              <Body dim size={compact ? 11.5 : 12.5} style={{ marginTop: -3, lineHeight: 1.5 }}>
                A nickname is fine — it’s just how we’ll address you.
              </Body>
            </>
          )}
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="Email address"
            aria-label="Email address"
            value={email}
            onChange={(e) => { setEmail(e.target.value); reset(); }}
            style={field}
          />
          {/* Forced organisation-code DECISION (create mode): a conscious Yes/No so
              workplace/organisation users can't accidentally skip redeeming. The email
              button stays disabled until they answer (and, if Yes, enter the code).
              Was a faint collapsed toggle users kept missing, signing up as free/trial. */}
          {collectName && (
            <>
              <Body dim size={compact ? 11.5 : 12.5} style={{ marginTop: 3, lineHeight: 1.5 }}>
                Do you have an organisation or access code?
              </Body>
              <div style={{ display: 'flex', gap }}>
                <Btn full variant={hasOrgCode === 'no' ? undefined : 'ghost'} size={size} onClick={() => { setHasOrgCode('no'); setOrgCode(''); reset(); }} ariaLabel="No, I do not have a code">
                  No
                </Btn>
                <Btn full variant={hasOrgCode === 'yes' ? undefined : 'ghost'} size={size} onClick={() => { setHasOrgCode('yes'); reset(); }} ariaLabel="Yes, I have an organisation or access code">
                  Yes
                </Btn>
              </div>
              {hasOrgCode === 'yes' && (
                <input
                  type="text"
                  autoCapitalize="characters"
                  autoComplete="off"
                  autoFocus
                  placeholder="Organisation or access code"
                  aria-label="Organisation or access code"
                  value={orgCode}
                  onChange={(e) => { setOrgCode(e.target.value.toUpperCase()); reset(); }}
                  style={{ ...field, textTransform: 'uppercase', letterSpacing: '0.06em' }}
                />
              )}
            </>
          )}
          <Btn
            full
            size={size}
            onClick={() => void onSendCode()}
            disabled={
              busy || sending || !email.trim() || (collectName && !firstName.trim()) ||
              (collectName && hasOrgCode === 'unset') ||
              (collectName && hasOrgCode === 'yes' && !orgCode.trim())
            }
          >
            {sending ? 'Sending…' : 'Email me a sign-in code'}
          </Btn>
        </>
      ) : (
        <>
          <input
            type={reviewMode ? 'password' : 'text'}
            inputMode={reviewMode ? 'text' : 'numeric'}
            autoComplete={reviewMode ? 'current-password' : 'one-time-code'}
            maxLength={reviewMode ? 64 : 6}
            placeholder={reviewMode ? 'Review PIN' : '6-digit code'}
            aria-label={reviewMode ? 'Review PIN' : '6-digit sign-in code'}
            value={otp}
            onChange={(e) => { setOtp(reviewMode ? e.target.value : e.target.value.replace(/\D/g, '').slice(0, 6)); reset(); }}
            style={{ ...field, letterSpacing: reviewMode ? 'normal' : '0.3em', textAlign: 'center', fontSize: 18 }}
          />
          {/* 6 either way: a 6-digit emailed code, or the review PIN, whose minimum is
              set by Supabase's own minimum password length (6). Matching it here stops a
              short PIN looking accepted and then failing at the server. */}
          <Btn full size={size} onClick={onVerify} disabled={busy || otp.trim().length < 6}>
            {reviewMode ? 'Sign in' : 'Verify code'}
          </Btn>
          <div style={{ display: 'flex', gap: 9 }}>
            {/* No inbox to resend to on the review path. */}
            {!reviewMode && (
              <Btn full variant="ghost" size={compact ? 'sm' : 'md'} onClick={() => void onSendCode()} disabled={busy || sending}>
                {sending ? 'Sending…' : 'Resend code'}
              </Btn>
            )}
            <Btn
              full
              variant="ghost"
              size={compact ? 'sm' : 'md'}
              onClick={() => { setOtpSent(false); setOtp(''); reset(); }}
              disabled={busy || sending}
            >
              Use a different email
            </Btn>
          </div>
          <Body dim size={12.5} style={{ marginTop: 1 }}>
            We sent a 6-digit code to {email.trim() || 'your email'}. Enter it above to sign in.
          </Body>
        </>
      )}

      {busy && (
        <Body dim size={12.5} style={{ textAlign: 'center', marginTop: 2 }}>
          One moment, signing you in.
        </Body>
      )}
      {!busy && auth.error && (
        <Body role="alert" size={13} style={{ marginTop: 2, color: 'var(--amber-deep)', lineHeight: 1.5 }}>
          {auth.error}
        </Body>
      )}

      <Body dim size={compact ? 11.5 : 12.5} style={{ marginTop: 2, lineHeight: 1.55 }}>
        {/* Native must not read as a pointer to an external purchase: "available on our
            website" is a buy prompt, "managed on the ReNurture website" is a statement
            of fact about where the account lives. Web keeps the selling version. */}
        {SHOW_PURCHASE
          ? 'Your free account keeps your progress saved across your devices. Membership unlocks everything, and is available on our website.'
          : SHOW_IAP
            ? 'Your free account keeps your progress saved across your devices. Membership unlocks everything, and you can join from inside the app.'
            : 'Your free account keeps your progress saved across your devices. Membership unlocks everything, and is managed on the ReNurture website.'}
      </Body>
    </div>
  );
}
